import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import LoadingButton from 'components/common/LoadingButton';
import { Can } from 'routes/permissions/Can';
import { userActions } from 'routes/permissions/permissionKeys';
import {
  useCreateDonationLink,
  useDeleteDonationLink,
  useDonationLinks,
  useDonationTypes,
  usePackages
} from 'hooks/useDonationSettings';

// Where the public donation site lives; the link is this plus the slug
export const DONATION_SITE_URL = (
  process.env.REACT_APP_DONATION_SITE_URL || 'https://donate.helpyateem.org'
).replace(/\/$/, '');

// Paths the donation site already uses, so a link can't shadow them
const RESERVED_SLUGS = [
  'donate',
  'profile',
  'reset-password',
  'api',
  'admin',
  'app',
  'www'
];

// "Qurbani Somalia (Whole Cow £317)" -> "qurbani-somalia-whole-cow-317"
export const slugify = text =>
  (text || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');

export const slugProblem = (slug, existing = []) => {
  if (!slug) return 'Enter a link name';
  if (slug.length < 2) return 'Link name is too short';
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug))
    return 'Use only lowercase letters, numbers and single dashes';
  if (RESERVED_SLUGS.includes(slug)) return 'That name is used by the site';
  if (existing.includes(slug)) return 'That link name is already in use';
  return null;
};

const linkUrl = slug => `${DONATION_SITE_URL}/${slug}`;

const copyLink = slug => {
  navigator.clipboard?.writeText(linkUrl(slug));
  toast.success('Link copied');
};

/**
 * Short links for the public donation site that open it with a campaign
 * (package) and donation type already selected, e.g. for posters and social
 * media. The donation site reads donation_links by slug.
 */
const DonationLinks = ({ subject }) => {
  const { donationLinks, isLoading } = useDonationLinks();
  const { packages } = usePackages({ is_hidden: false });
  const { donationTypes } = useDonationTypes();
  const { createLinkAsync, isCreating } = useCreateDonationLink();
  const { deleteLink, isDeleting } = useDeleteDonationLink();

  const [packageId, setPackageId] = useState('');
  const [donationTypeId, setDonationTypeId] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [showError, setShowError] = useState(false);

  const existingSlugs = useMemo(
    () => donationLinks.map(l => l.slug),
    [donationLinks]
  );

  // Suggest the link name from the campaign until the user types their own
  useEffect(() => {
    if (slugEdited) return;
    const pkg = packages?.find(p => p.id === packageId);
    setSlug(slugify(pkg?.package_title));
  }, [packageId, packages, slugEdited]);

  const problem = !packageId
    ? 'Choose a campaign'
    : slugProblem(slug, existingSlugs);

  const handleCreate = async () => {
    setShowError(true);
    if (problem) return;
    try {
      await createLinkAsync({
        slug,
        package_id: packageId,
        donation_type_id: donationTypeId || null
      });
      setPackageId('');
      setDonationTypeId('');
      setSlug('');
      setSlugEdited(false);
      setShowError(false);
    } catch {
      // toast shown by the hook
    }
  };

  return (
    <Card className="border shadow-none mb-3" id="donation-links">
      <Card.Header>
        <Card.Title>Custom donation links</Card.Title>
        <p className="text-muted fs--1 mb-0">
          Short links for the donation site that open with a campaign and
          donation type already selected. Share them on posters, social media
          and WhatsApp.
        </p>
      </Card.Header>
      <Card.Body>
        <Can I={userActions.update} a={subject}>
          <Row className="g-3 align-items-end mb-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Campaign</Form.Label>
                <Form.Select
                  value={packageId}
                  onChange={e => setPackageId(e.target.value)}
                >
                  <option value="">Select campaign...</option>
                  {packages?.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.package_title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Donation type</Form.Label>
                <Form.Select
                  value={donationTypeId}
                  onChange={e => setDonationTypeId(e.target.value)}
                >
                  <option value="">Site default</option>
                  {donationTypes?.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.donation_type_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Link name</Form.Label>
                <Form.Control
                  value={slug}
                  placeholder="e.g. ramadan"
                  isInvalid={showError && !!problem && !!packageId}
                  onChange={e => {
                    setSlugEdited(true);
                    setSlug(slugify(e.target.value));
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                />
                <Form.Control.Feedback type="invalid">
                  {problem}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={2}>
              <LoadingButton
                type="button"
                className="w-100"
                loading={isCreating}
                onClick={handleCreate}
              >
                Create link
              </LoadingButton>
            </Col>
          </Row>
          <p className="text-600 fs--1 mb-4">
            Link will be <code>{linkUrl(slug || 'link-name')}</code>
            {showError && problem && !packageId && (
              <span className="text-danger ms-2">{problem}</span>
            )}
          </p>
        </Can>

        {isLoading ? (
          <p className="text-muted mb-0">Loading links...</p>
        ) : donationLinks.length === 0 ? (
          <p className="text-muted mb-0">No custom links yet.</p>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="fs--1 mb-0 align-middle">
              <thead className="bg-200 text-900">
                <tr>
                  <th>Link</th>
                  <th>Campaign</th>
                  <th>Donation type</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {donationLinks.map(link => (
                  <tr key={link.id}>
                    <td>
                      {/* Full link on wide screens, just the path on phones */}
                      <code className="d-none d-md-inline">
                        {linkUrl(link.slug)}
                      </code>
                      <code className="d-md-none">/{link.slug}</code>
                      <Button
                        size="sm"
                        variant="link"
                        className="py-0 px-2"
                        title="Copy link"
                        onClick={() => copyLink(link.slug)}
                      >
                        <FontAwesomeIcon icon="copy" className="me-1" />
                        Copy
                      </Button>
                    </td>
                    <td>{link.package?.package_title || '-'}</td>
                    <td>
                      {link.donation_type?.donation_type_name || (
                        <span className="text-500">Site default</span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      {link.created_at
                        ? format(new Date(link.created_at), 'dd MMM yyyy')
                        : '-'}
                    </td>
                    <td className="text-end">
                      <Can I={userActions.update} a={subject}>
                        <Button
                          size="sm"
                          variant="falcon-danger"
                          className="py-0 px-2"
                          title="Delete link"
                          disabled={isDeleting}
                          onClick={() =>
                            window.confirm(
                              `Delete ${linkUrl(
                                link.slug
                              )}? Anyone using it will see the normal donation page.`
                            ) && deleteLink(link.id)
                          }
                        >
                          <FontAwesomeIcon icon="trash" />
                        </Button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

DonationLinks.propTypes = {
  subject: PropTypes.string
};

export default DonationLinks;
